import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Button, Box, Divider } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const WalletDebug: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [debugInfo, setDebugInfo] = useState<any>({});

  // Fetch wallet using the same logic as Navbar
  const { data: walletData, error: walletError, isLoading, refetch, status, fetchStatus } = useQuery({
    queryKey: ['wallet', user?.id],
    queryFn: async () => {
      console.log('🔄 WALLET QUERY EXECUTING for user ID:', user?.id);
      if (!user?.id) {
        console.log('❌ No user ID, returning null');
        return null;
      }
      console.log('📡 Making API call to /wallet/user/' + user.id);
      const response = await axios.get(`/wallet/user/${user.id}`);
      console.log('✅ Wallet API response:', response.data);
      return response.data;
    },
    enabled: !!user?.id,
    retry: 1,
  });

  // Check if query is idle (not running because disabled)
  const isIdle = status === 'pending' && fetchStatus === 'idle';

  useEffect(() => {
    // Gather all debug information
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    // Safely extract error information
    let errorInfo = null;
    if (walletError) {
      const err = walletError as any;
      errorInfo = err.response?.data || err.message || String(walletError);
    }
    
    setDebugInfo({
      isAuthenticated,
      user,
      storedUser: storedUser ? JSON.parse(storedUser) : null,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 50) + '...' : 'No token',
      walletData,
      walletError: errorInfo,
      queryKey: ['wallet', user?.id],
      queryEnabled: !!user?.id,
      userIdExists: user?.id !== undefined && user?.id !== null,
      userIdValue: user?.id,
      isQueryIdle: isIdle,
      queryStatus: status,
      queryFetchStatus: fetchStatus,
    });
  }, [isAuthenticated, user, walletData, walletError, isIdle, status, fetchStatus]);

  const handleRefresh = () => {
    refetch();
  };

  const handleClearCache = () => {
    queryClient.clear();
    window.location.reload();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Wallet Debug Information
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>Authentication Status</Typography>
          <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
            {JSON.stringify({
              isAuthenticated,
              hasToken: debugInfo.hasToken,
            }, null, 2)}
          </pre>
        </Box>

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>User from useAuth()</Typography>
          <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
            {JSON.stringify(debugInfo.user, null, 2)}
          </pre>
        </Box>

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>User from localStorage</Typography>
          <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
            {JSON.stringify(debugInfo.storedUser, null, 2)}
          </pre>
        </Box>

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>Token Preview</Typography>
          <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
            {debugInfo.tokenPreview}
          </pre>
        </Box>

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>
            Wallet Query Status
            {isLoading && ' (Loading...)'}
            {isIdle && ' (IDLE - NOT RUNNING)'}
          </Typography>
          <pre style={{ background: isIdle ? '#ffe6e6' : '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
            {JSON.stringify({
              queryKey: debugInfo.queryKey,
              queryEnabled: debugInfo.queryEnabled,
              userIdExists: debugInfo.userIdExists,
              userIdValue: debugInfo.userIdValue,
              queryStatus: debugInfo.queryStatus,
              queryFetchStatus: debugInfo.queryFetchStatus,
              isIdle: isIdle,
              isLoading,
              hasData: !!walletData,
              hasError: !!walletError,
              reasonNotRunning: !debugInfo.queryEnabled ? 'User ID is falsy (null/undefined/0)' : null,
            }, null, 2)}
          </pre>
        </Box>

        {isIdle && (
          <Box sx={{ my: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.main" fontWeight="bold">
              ⚠️ WALLET QUERY IS NOT RUNNING
            </Typography>
            <Typography variant="body2">
              The wallet API call is not being made because the query is idle. This usually means:
            </Typography>
            <ul>
              <li>User ID is null, undefined, or 0</li>
              <li>User authentication data is missing</li>
              <li>The query is disabled due to missing conditions</li>
            </ul>
          </Box>
        )}

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>Wallet Data</Typography>
          <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
            {JSON.stringify(walletData, null, 2)}
          </pre>
        </Box>

        {walletError && (
          <Box sx={{ my: 2 }}>
            <Typography variant="h6" gutterBottom color="error">Wallet Error</Typography>
            <pre style={{ background: '#fff3cd', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
              {JSON.stringify(debugInfo.walletError, null, 2)}
            </pre>
          </Box>
        )}

        <Box sx={{ my: 2, display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleRefresh}>
            Refresh Wallet Data
          </Button>
          <Button variant="outlined" onClick={handleClearCache}>
            Clear Cache & Reload
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => {
              console.log('=== WALLET DEBUG INFO ===');
              console.log('User:', debugInfo.user);
              console.log('Wallet Data:', walletData);
              console.log('Wallet Error:', walletError);
              console.log('=========================');
            }}
          >
            Log to Console
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>Instructions</Typography>
          <Typography variant="body2" paragraph>
            1. Check if the User ID from useAuth() matches your actual user ID in the database
          </Typography>
          <Typography variant="body2" paragraph>
            2. Verify that the wallet query is using the correct user ID
          </Typography>
          <Typography variant="body2" paragraph>
            3. If there's a 404 error, it means no wallet exists for this user ID
          </Typography>
          <Typography variant="body2" paragraph>
            4. If the user ID is wrong, you may need to logout and login again
          </Typography>
          <Typography variant="body2" paragraph>
            5. Check the browser console for additional error messages
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default WalletDebug;

